/**
 * POS Products Sync Bar Component
 *
 * Displays sync status and provides manual sync trigger.
 */

import { RefreshCw, Clock, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui';
import type { PosSyncStatusInfo } from '../services/pos-products.service';
import type { SSEProgressEvent } from '@bstock/shared';

interface PosProductsSyncBarProps {
  syncStatus: PosSyncStatusInfo | undefined;
  isLoading: boolean;
  onSync: () => void;
  isSyncing: boolean;
  /** Progression SSE temps reel (si la sync est en mode stream). */
  progress?: SSEProgressEvent | null;
  /** Bouton d'annulation de la sync en cours. */
  onCancel?: () => void;
}

const STEP_LABELS: Record<string, string> = {
  init: 'Demarrage...',
  fetch_products: 'Recuperation des produits',
  fetch_options: 'Recuperation des options',
  upsert: 'Enregistrement en base',
  done: 'Termine',
};

function describeProgress(progress: SSEProgressEvent | null | undefined): string {
  if (!progress) return 'Initialisation...';
  const stepLabel = STEP_LABELS[progress.step] ?? progress.step;
  const details = progress.details as { message?: string } | undefined;
  if (details?.message) return details.message;
  return stepLabel;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return 'Jamais';
  return new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(dateString));
}

function getSyncStatusIcon(status: PosSyncStatusInfo['lastSyncStatus']) {
  switch (status) {
    case 'SYNCED':
      return <CheckCircle2 className="h-5 w-5 text-success-500" />;
    case 'PENDING':
      return <Clock className="h-5 w-5 text-warning-500" />;
    case 'ERROR':
      return <AlertTriangle className="h-5 w-5 text-danger-500" />;
    default:
      return <Clock className="h-5 w-5 text-neutral-400" />;
  }
}

export function PosProductsSyncBar({
  syncStatus,
  isLoading,
  onSync,
  isSyncing,
  progress,
  onCancel,
}: PosProductsSyncBarProps) {
  const progressPct = progress?.progress ?? 0;
  const progressLabel = describeProgress(progress);

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-6">
          {/* Sync status */}
          <div className="flex items-center gap-2">
            {isLoading ? (
              <RefreshCw className="h-5 w-5 text-neutral-400 animate-spin" />
            ) : (
              getSyncStatusIcon(syncStatus?.lastSyncStatus ?? null)
            )}
            <div>
              <p className="text-sm font-medium text-dark-500">
                {syncStatus?.lastSyncStatus === 'SYNCED'
                  ? 'Synchronise'
                  : syncStatus?.lastSyncStatus === 'ERROR'
                    ? 'Erreur de synchronisation'
                    : 'En attente'}
              </p>
              <p className="text-xs text-neutral-500">
                Derniere sync : {formatDate(syncStatus?.lastSyncAt ?? null)}
              </p>
            </div>
          </div>

          {/* Products count */}
          <div className="border-l border-neutral-200 pl-6">
            <p className="text-2xl font-bold text-dark-500">
              {syncStatus?.productsCount ?? '-'}
            </p>
            <p className="text-xs text-neutral-500">Produits POS</p>
          </div>
        </div>

        {/* Sync button */}
        <div className="flex items-center gap-2">
          <Button
            onClick={onSync}
            disabled={isSyncing}
            variant="primary"
            size="md"
            title={
              isSyncing
                ? undefined
                : 'La synchronisation peut prendre plusieurs minutes (limite Bill : 1 requete / 20s)'
            }
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`}
            />
            {isSyncing ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
          {isSyncing && onCancel && (
            <Button onClick={onCancel} variant="ghost" size="md" aria-label="Annuler">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar (SSE) */}
      {isSyncing && (
        <div className="mt-4 space-y-2">
          <div className="flex justify-between text-xs text-neutral-600">
            <span>{progressLabel}</span>
            <span className="font-mono">{progressPct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-full bg-primary-500 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-neutral-500">
            Bill API limite a 1 requete / 20s — la synchro complete peut durer 3 a 5 minutes.
          </p>
        </div>
      )}

      {/* Error message */}
      {!isSyncing && syncStatus?.lastSyncError && (
        <div className="mt-3 p-3 bg-danger-50 border border-danger-200 rounded-lg">
          <p className="text-sm text-danger-700">{syncStatus.lastSyncError}</p>
        </div>
      )}
    </div>
  );
}
